import simbApi from ".";
import {
  GET_ACCOUNT_SETTINGS,
  UPDATE_ACCOUNT_SETTING,
  VISUAL_INDICATOR,
  VISUAL_INDICATOR_ESTIMATE,
  VISUAL_INDICATOR_STATUS,
  VISUAL_INDICATOR_STOP,
} from "../../data/constants/endpoints";
import type {
  VisualIndicatorJobStatus,
  VisualIndicatorMode,
  VisualIndicatorSetting,
} from "../../components/Navbar/Settings/types/settings-drawer-interface";

export const getAccountSettings = async (access_token: string, accountId: string) => {
  try {
    const response = await simbApi.get(GET_ACCOUNT_SETTINGS, {
      headers: { Authorization: `Bearer ${access_token}` },
    });

    return response.data.data;
  } catch (error) {
    throw error;
  }
};

export const updateSetting = async (access_token: string, accountId: string, settingKey: string, value: boolean) => {
  try {
    const response = await simbApi.post(
      UPDATE_ACCOUNT_SETTING,
      {
        accountId,
        settingKey,
        value,
      },
      {
        headers: { Authorization: `Bearer ${access_token}` },
      }
    );

    return response;
  } catch (error) {
    throw error;
  }
};

const authHeaders = (accessToken: string) => ({ headers: { Authorization: `Bearer ${accessToken}` } });

// The settings proxies pass app-ms's `{ statusCode, data }` envelope through; the 202 routes
// answer flat. Accept either.
const unwrap = <T>(body: any): T =>
  body && typeof body === "object" && body.data && typeof body.data === "object" && !Array.isArray(body.data)
    ? (body.data as T)
    : (body as T);

export interface SaveVisualIndicatorResult {
  /** swap / remove (202): the bulk rename that was queued. */
  requestId?: string;
  estimatedItems?: number;
  /** save (200). */
  indicator?: VisualIndicatorSetting;
  token?: string;
}

export const saveVisualIndicator = async (
  accessToken: string,
  body: { mode: VisualIndicatorMode; indicator?: VisualIndicatorSetting },
): Promise<SaveVisualIndicatorResult> => {
  const response = await simbApi.post(VISUAL_INDICATOR, body, authHeaders(accessToken));
  return unwrap<SaveVisualIndicatorResult>(response.data) ?? {};
};

export const getVisualIndicatorEstimate = async (accessToken: string): Promise<number> => {
  const response = await simbApi.post(VISUAL_INDICATOR_ESTIMATE, {}, authHeaders(accessToken));
  return Number(unwrap<{ estimatedItems?: number }>(response.data)?.estimatedItems ?? 0);
};

/** The I4 data block, or `{ status: "unknown" }` once the request's state has expired. */
export const getVisualIndicatorStatus = async (
  accessToken: string,
  requestId: string,
): Promise<VisualIndicatorJobStatus | { status: "unknown" }> => {
  const response = await simbApi.post(VISUAL_INDICATOR_STATUS, { requestId }, authHeaders(accessToken));
  return unwrap<VisualIndicatorJobStatus | { status: "unknown" }>(response.data);
};

export const stopVisualIndicator = async (accessToken: string, requestId: string): Promise<void> => {
  await simbApi.post(VISUAL_INDICATOR_STOP, { requestId }, authHeaders(accessToken));
};
